import { neckDiagramShareHref } from "../neckDiagrams/api.js";
import { useEffect, useState } from "react";
import { PracticeTaskMaterials } from "../PracticeTaskMaterials.js";
import { Link, useLocation } from "react-router-dom";
import { practiceTaskAnchor, practiceToolHref } from "../practiceToolContext.js";
import { formatDate, formatDateTime, formatDuration } from "../format.js";
import { grooveSnapshotHref } from "../grooves.js";
import {
  getFamilyPracticeTodos,
  setFamilyPracticeTodoCompleted,
  type PracticeTodo,
} from "../practiceTodos.js";
import { useAsync } from "../useAsync.js";
import "../familyLessonNotes.css";

export function FamilyPracticeTodos({
  studentId,
  timerRunning = false,
  activeTodoId = null,
  practiceBusy = false,
  revision = "",
  onPractise,
  onLogPractice,
  toolLinks = false,
  learnerInstruments,
}: {
  studentId: string;
  timerRunning?: boolean;
  activeTodoId?: string | null;
  practiceBusy?: boolean;
  revision?: string;
  onPractise?: (todo: PracticeTodo) => void | Promise<void>;
  onLogPractice?: (todo: PracticeTodo) => void;
  /** Link each task into the metronome and its Groove with learner, instrument and a way back. */
  toolLinks?: boolean;
  /** The learner's verified lesson instruments, when known: a task's instrument outside them is not passed on. */
  learnerInstruments?: readonly string[];
}) {
  const todos = useAsync(() => getFamilyPracticeTodos(studentId), [studentId, revision]);
  const { hash } = useLocation();
  // Coming back from a tool lands on the task that was open, without changing anything.
  useEffect(() => {
    if (!toolLinks || !hash.startsWith("#practice-task-") || !todos.data) return;
    const row = document.getElementById(decodeURIComponent(hash.slice(1)));
    row?.scrollIntoView({ block: "center" });
    row?.focus({ preventScroll: true });
  }, [hash, toolLinks, todos.data]);
  const taskContext = (todo: PracticeTodo) => ({
    studentId,
    todoId: todo.id,
    instrument: todo.instrument && (!learnerInstruments || learnerInstruments.includes(todo.instrument)) ? todo.instrument : null,
    returnTo: `/practice#${practiceTaskAnchor(todo.id)}`,
  });
  /*
   * Work still being set, and work that is only a record now.
   *
   * A task can outlive the groove behind it — the child practised it, or a
   * tutor's goal points at it — and deleting it would rewrite what they did.
   * But it is no longer something they are being asked to do, and leaving it
   * among the live tasks says the opposite. Kept, shown, and plainly marked.
   */
  // Only a date withdraws work. A payload without the field at all — an older
  // API answering during a rolling deploy, or a cached response — must read as
  // live work, never quietly move a child's homework into the archive.
  const live = (todos.data ?? []).filter((todo) => !todo.withdrawnAt && !todo.completed);
  const completed = (todos.data ?? []).filter(todo => todo.completed && !todo.withdrawnAt);
  const withdrawn = (todos.data ?? []).filter((todo) => Boolean(todo.withdrawnAt));
  const [busyId, setBusyId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function toggle(todo: PracticeTodo) {
    if (busyId !== null) return;
    setBusyId(todo.id);
    setProblem(null);
    try {
      await setFamilyPracticeTodoCompleted(studentId, todo.id, !todo.readyForReviewAt);
      todos.reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not update that practice task.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <section aria-labelledby="practice-todo-heading" className="family-practice-todos-section">
        <div className="practice-section-heading">
          <h2 id="practice-todo-heading">Assigned practice</h2>
          {todos.data && <span className="small muted">{live.filter((todo) => !todo.completed).length} to work on</span>}
        </div>
        {todos.loading && !todos.data && <p className="small muted">Loading practice to-dos…</p>}
        {todos.error && <div className="error small">{todos.error}</div>}
        {problem && <div className="error small">{problem}</div>}
        {todos.data && live.length === 0 && withdrawn.length === 0 && completed.length === 0 && (
          <div className="panel">
            <p className="small muted family-practice-todos-empty">No practice to-dos have been set yet.</p>
          </div>
        )}
        {todos.data && live.length === 0 && (withdrawn.length > 0 || completed.length > 0) && (
          <div className="panel">
            <p className="small muted family-practice-todos-empty">
              No practice is currently set. What you have already worked on is below.
            </p>
          </div>
        )}
        {live.length > 0 && (
          <div className="panel stack family-practice-todos-stack">
            {live.map((todo) => {
              const active = timerRunning && activeTodoId === todo.id;
              const checkboxId = `practice-todo-${todo.id}`;
              const practiceSeconds = Number.isFinite(todo.practiceSeconds) ? todo.practiceSeconds : 0;
              const latestComment = todo.comments?.[todo.comments.length - 1];
              return (
                <div
                  className={`row family-practice-todo-row${active ? " is-active" : ""}${todo.completed ? " is-completed" : ""}`}
                  key={todo.id}
                  id={toolLinks ? practiceTaskAnchor(todo.id) : undefined}
                  tabIndex={toolLinks ? -1 : undefined}
                >
                  <input
                    id={checkboxId}
                    type="checkbox"
                    checked={Boolean(todo.readyForReviewAt)}
                    disabled={busyId !== null}
                    onChange={() => void toggle(todo)}
                    aria-label={`${todo.readyForReviewAt ? "Keep practising" : "Ready for tutor review"}: ${todo.title}`}
                    className="family-practice-todo-check"
                  />
                  <label htmlFor={checkboxId} className="family-practice-todo-copy">
                    <strong className={todo.completed ? "muted" : undefined}>{todo.title}</strong>
                    {(todo.suggestedMinutes != null || practiceSeconds > 0) && (
                      <span className="small muted family-practice-todo-meta">
                        {todo.suggestedMinutes != null && <>Aim for {todo.suggestedMinutes} min/day</>}
                        {todo.suggestedMinutes != null && practiceSeconds > 0 && " · "}
                        {practiceSeconds > 0 && <>Practised {formatDuration(practiceSeconds)}</>}
                      </span>
                    )}
                    {todo.repertoireTitle && (
                      <span className="small muted family-practice-todo-meta">
                        Repertoire: {todo.repertoireComposer ? `${todo.repertoireComposer} — ` : ""}
                        {todo.repertoireTitle}
                      </span>
                    )}
                    {todo.description && (
                      <span className="small muted family-practice-todo-meta">{todo.description}</span>
                    )}
                    <span className="small family-practice-todo-meta">
                      {todo.readyForReviewAt
                        ? `Ready for tutor review — your tutor will sign it off. Sent ${formatDateTime(todo.readyForReviewAt)}.`
                        : latestComment
                          ? `Tutor feedback received ${formatDateTime(latestComment.publishedAt)}. Add another recording in recent practice and tick when ready to send an update.`
                          : "Tick when your practice or recording is ready for tutor review."}
                    </span>
                    <span className="small muted family-practice-todo-meta">
                      Practice history keeps each recording with its session and shows when it was added. Adding another recording adds a new history entry.
                    </span>
                    <TaskFeedback todo={todo} />
                  </label>
                  <div className="practice-task-actions">
                  {onPractise && !todo.completed && (
                    <button
                      type="button"
                      className={!timerRunning && live.find((item) => !item.completed)?.id === todo.id ? "small practice-task-start" : "quiet small practice-task-start"}
                      disabled={practiceBusy || active}
                      aria-pressed={active}
                      onClick={() => void onPractise(todo)}
                    >
                      {active ? "Practising now" : timerRunning ? "Switch to this" : "Start practice"}
                    </button>
                  )}
                    {onLogPractice && !todo.completed && (
                      <button type="button" className="quiet small" disabled={timerRunning || practiceBusy} onClick={() => onLogPractice(todo)}>
                        {timerRunning ? "Finish timer to log separately" : "Log practice"}
                      </button>
                    )}
                    <PracticeTaskMaterials materials={todo.materials} practiceTarget={todo.id} />
                    {todo.metronome && (
                      <Link
                        className="small family-practice-todo-link"
                        to={toolLinks ? practiceToolHref("/practice/metronome", { ...taskContext(todo), as: "todo" }) : `/practice/metronome?todo=${encodeURIComponent(todo.id)}`}
                      >
                        Open metronome · {todo.metronome.bpm} BPM · {todo.metronome.signature}
                      </Link>
                    )}
                    {toolLinks && !todo.metronome && !todo.completed && (
                      <Link className="small family-practice-todo-link" to={practiceToolHref("/practice/metronome", taskContext(todo))}>
                        Open metronome
                      </Link>
                    )}
                    {todo.neckDiagram && <Link className="small" to={neckDiagramShareHref(todo.neckDiagram.shareId)}>Open neck diagram “{todo.neckDiagram.title}”</Link>}
                    {todo.groove && !todo.materials?.some(material => material.groove?.query === todo.groove?.query && material.groove?.title === todo.groove?.title) && (
                      <Link
                        className="small family-practice-todo-link"
                        to={toolLinks ? practiceToolHref(grooveSnapshotHref(todo.groove.token), taskContext(todo)) : grooveSnapshotHref(todo.groove.token)}
                      >
                        Open and play “{todo.groove.title}”
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {completed.length > 0 && (
        <details className="panel family-practice-todos-section">
          <summary>Completed tasks ({completed.length})</summary>
          {completed.map(todo => <div className="family-practice-todo-copy" key={todo.id}>
            <strong>{todo.title}</strong>
            <PracticeTaskMaterials materials={todo.materials} practiceTarget={todo.id} />
            <span className="small muted family-practice-todo-meta">Completed{todo.completedAt ? ` · ${formatDate(todo.completedAt)}` : ""}</span>
            <TaskFeedback todo={todo} />
          </div>)}
        </details>
      )}
      {withdrawn.length > 0 && (
        <section
          aria-labelledby="practice-todo-withdrawn-heading"
          className="family-practice-todos-section"
        >
          <h2 id="practice-todo-withdrawn-heading">No longer assigned</h2>
          <p className="small muted family-practice-todos-empty">
            Your tutor has moved on from these. They are here so your practice time stays with
            them — there is nothing left to tick off.
          </p>
          <div className="panel stack family-practice-todos-stack family-practice-todos-withdrawn">
            {withdrawn.map((todo) => {
              const practiceSeconds = Number.isFinite(todo.practiceSeconds)
                ? todo.practiceSeconds
                : 0;
              return (
                <div className="row family-practice-todo-row is-withdrawn" key={todo.id}>
                  <div className="family-practice-todo-copy">
                    <strong className="muted">{todo.title}</strong>
                    <span className="tag family-practice-todo-withdrawn-tag">
                      No longer assigned
                    </span>
                    {practiceSeconds > 0 && (
                      <span className="small muted family-practice-todo-meta">
                        Practised {formatDuration(practiceSeconds)}
                      </span>
                    )}
                    {todo.repertoireTitle && (
                      <span className="small muted family-practice-todo-meta">
                        Repertoire: {todo.repertoireComposer ? `${todo.repertoireComposer} — ` : ""}
                        {todo.repertoireTitle}
                      </span>
                    )}
                    {/* The groove still opens: the steps were kept with it. */}
                    <PracticeTaskMaterials materials={todo.materials} practiceTarget={todo.id} />
                    {todo.groove && (
                      <Link
                        className="small family-practice-todo-link"
                        to={grooveSnapshotHref(todo.groove.token)}
                      >
                        Look back at “{todo.groove.title}”
                      </Link>
                    )}
                    {todo.completed && todo.completedAt && (
                      <span className="small muted family-practice-todo-meta">
                        Completed {formatDate(todo.completedAt)}
                      </span>
                    )}
                    {todo.withdrawnAt && (
                      <span className="small muted family-practice-todo-meta">
                        No longer set from {formatDate(todo.withdrawnAt)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

    </>
  );
}

function TaskFeedback({ todo }: { todo: PracticeTodo }) {
  if (!todo.comments?.length) return null;
  return <span className="family-practice-task-feedback">
    <strong className="small">Task feedback</strong>
    {todo.comments.map((comment, index) => <span className="small family-practice-todo-meta" key={`${comment.publishedAt}:${index}`}>
      <span className="muted">{comment.author} · {formatDate(comment.publishedAt)}</span><br />{comment.comment}
    </span>)}
  </span>;
}
